import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "../../api";
import { getApiHeaders } from "../../utils/helpers";
import MessageToster from "../MessageToster";

const FALLBACK_IMAGES = [
  "https://images.unsplash.com/photo-1579586337278-3befd40fd17a?auto=format&fit=crop&w=300&q=80",
  "https://images.unsplash.com/photo-1546868871-7041f2a55e12?auto=format&fit=crop&w=300&q=80",
  "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?auto=format&fit=crop&w=300&q=80",
  "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=300&q=80",
  "https://images.unsplash.com/photo-1563720223185-11003d516935?auto=format&fit=crop&w=300&q=80",
];

const getFallbackImage = (item) => {
  if (item?.image || item?.image_url) return item.image || item.image_url;
  const name = String(item?.reward_name || item?.title || "").toLowerCase();
  if (name.includes("pixel") || (name.includes("smartwatch") && name.includes("google"))) {
    return FALLBACK_IMAGES[0];
  }
  if (name.includes("apple") || name.includes("watch")) {
    return FALLBACK_IMAGES[1];
  }
  if (name.includes("iphone") || name.includes("phone")) {
    return FALLBACK_IMAGES[2];
  }
  if (name.includes("dubai") || name.includes("trip")) {
    return FALLBACK_IMAGES[3];
  }
  if (name.includes("rover") || name.includes("car")) {
    return FALLBACK_IMAGES[4];
  }
  return FALLBACK_IMAGES[0];
};

export default function ClaimRewardModal({ isOpen, reward, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setError("");
      setLoading(false);
    }
  }, [isOpen, reward]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen && !loading) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, loading, onClose]);

  if (!isOpen || !reward) return null;

  const rewardTitle =
    reward.reward_name || reward.title || reward.shortTitle || `Reward #${reward.id}`;
  const rewardImage = reward.image || reward.image_url || getFallbackImage(reward);
  const lotsRequired = Number(reward.lots_required ?? reward.lots ?? 0);
  const lotsTraded = Number(reward.lots_traded ?? reward.currentLots ?? 0);
  const rewardValue = Number(reward.reward_value ?? reward.rewardValue ?? 0);
  const rewardNumber = reward.number || (reward.sequence_no ? `#${reward.sequence_no}` : `#${reward.id}`);

  const handleClaimSubmit = async () => {
    if (!reward || reward.id === undefined || reward.id === null) {
      setError("Invalid reward selected.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload = {
        id: Number(reward.id) || reward.id,
      };

      const res = await axios.post(`${BASE_URL}/deposit/trade_earn_claim`, payload, {
        headers: getApiHeaders(),
      });

      const isSuccess =
        (res.data?.code === 200 || res.data?.status === "success" || res.data?.status === true) &&
        res.data?.code !== 400 &&
        res.data?.status !== "fail" &&
        res.data?.status !== "error";

      if (isSuccess) {
        const successMsg =
          res.data?.message || res.data?.msg || res.data?.data?.msg || "Reward claimed successfully!";
        MessageToster("success", successMsg);
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        const errMsg =
          res.data?.message ||
          res.data?.msg ||
          res.data?.data?.message ||
          res.data?.data?.msg ||
          "Failed to claim reward. Please try again.";
        setError(errMsg);
      }
    } catch (err) {
      console.error("Error claiming reward:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.msg ||
        err.response?.data?.data?.msg ||
        err.message ||
        "Something went wrong while claiming reward.";
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.65)",
        backdropFilter: "blur(5px)",
        zIndex: 1060,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) {
          onClose();
        }
      }}
    >
      <div className="modal-dialog modal-dialog-centered mx-3 mx-sm-auto" style={{ maxWidth: "480px" }}>
        <div
          className="modal-content border shadow-lg"
          style={{
            borderRadius: "16px",
            borderColor: "var(--bs-border-color)",
            backgroundColor: "var(--bs-secondary-bg)",
            overflow: "hidden",
          }}
        >
          {/* Modal Header */}
          <div
            className="d-flex align-items-center justify-content-between px-3 px-sm-4 py-3 border-bottom"
            style={{
              borderColor: "var(--bs-border-color)",
              backgroundColor: "var(--bs-secondary-bg)",
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style={{
                  width: "36px",
                  height: "36px",
                  backgroundColor: "rgba(var(--bs-primary-rgb), 0.12)",
                  color: "var(--bs-primary)",
                }}
              >
                <i className="bx bx-gift font-size-20"></i>
              </div>
              <div className="min-w-0">
                <h5
                  className="modal-title fw-bold mb-0 text-truncate"
                  style={{
                    fontSize: "16px",
                    color: "var(--bs-emphasis-color)",
                  }}
                >
                  Claim Reward
                </h5>
                <span className="font-size-11 text-muted text-truncate d-block">
                  Trade &amp; Earn Program {rewardNumber}
                </span>
              </div>
            </div>

            <button
              type="button"
              className="btn btn-link p-0 text-muted shadow-none"
              style={{
                color: "var(--bs-secondary-color)",
                lineHeight: "1",
              }}
              onClick={onClose}
              disabled={loading}
              aria-label="Close"
            >
              <i className="bx bx-x font-size-24"></i>
            </button>
          </div>

          {/* Modal Body */}
          <div className="modal-body p-3 p-sm-4">
            {/* Reward Card Preview */}
            <div
              className="card border mb-3"
              style={{
                borderRadius: "12px",
                backgroundColor: "var(--bs-tertiary-bg)",
                borderColor: "var(--bs-border-color)",
              }}
            >
              <div className="card-body p-3 d-flex align-items-center gap-3">
                <div
                  className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0 p-1"
                  style={{
                    width: "72px",
                    height: "72px",
                    backgroundColor: "rgba(var(--bs-primary-rgb), 0.08)",
                    border: "1px solid var(--bs-border-color)",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={rewardImage}
                    alt={rewardTitle}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = FALLBACK_IMAGES[0];
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "8px",
                    }}
                  />
                </div>

                <div className="flex-grow-1 overflow-hidden">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span
                      className="badge rounded-pill fw-bold font-size-10 px-2 py-0.5 text-white"
                      style={{ backgroundColor: "var(--bs-primary)" }}
                    >
                      UNLOCKED
                    </span>
                    {rewardValue > 0 && (
                      <span className="font-size-11 fw-semibold text-primary">
                        ${rewardValue.toLocaleString()} Value
                      </span>
                    )}
                  </div>
                  <h6
                    className="fw-bold mb-1 text-truncate"
                    style={{
                      fontSize: "14px",
                      color: "var(--bs-emphasis-color)",
                    }}
                    title={rewardTitle}
                  >
                    {rewardTitle}
                  </h6>
                  <span className="font-size-12 text-muted">
                    Target: {lotsRequired} Lots
                  </span>
                </div>
              </div>
            </div>

            {/* Reward Details List */}
            <div
              className="p-3 rounded-3 mb-3"
              style={{
                backgroundColor: "var(--bs-tertiary-bg)",
                border: "1px solid var(--bs-border-color)",
              }}
            >
              <div className="d-flex justify-content-between align-items-center py-1 border-bottom font-size-13" style={{ borderColor: "var(--bs-border-color)" }}>
                <span className="text-muted">Reward ID</span>
                <span className="fw-semibold" style={{ color: "var(--bs-emphasis-color)" }}>
                  #{reward.id}
                </span>
              </div>
              <div className="d-flex justify-content-between align-items-center py-1.5 border-bottom font-size-13" style={{ borderColor: "var(--bs-border-color)" }}>
                <span className="text-muted">Lots Required</span>
                <span className="fw-semibold" style={{ color: "var(--bs-emphasis-color)" }}>
                  {lotsRequired} Lots
                </span>
              </div>
              {lotsTraded > 0 && (
                <div className="d-flex justify-content-between align-items-center py-1.5 border-bottom font-size-13" style={{ borderColor: "var(--bs-border-color)" }}>
                  <span className="text-muted">Lots Traded</span>
                  <span className="fw-semibold" style={{ color: "var(--bs-emphasis-color)" }}>
                    {lotsTraded.toFixed(2)} Lots
                  </span>
                </div>
              )}
              <div className="d-flex justify-content-between align-items-center pt-1.5 font-size-13">
                <span className="text-muted">Status</span>
                <span
                  className="badge rounded-pill fw-semibold font-size-11 px-2.5 py-1"
                  style={{
                    backgroundColor: "rgba(var(--bs-primary-rgb), 0.12)",
                    color: "var(--bs-primary)",
                  }}
                >
                  <i className="bx bx-check-circle me-1 font-size-12"></i>
                  Ready to Claim
                </span>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div
                className="alert alert-danger py-2 px-3 mb-3 font-size-12 d-flex align-items-center gap-2"
                role="alert"
              >
                <i className="bx bx-error-circle font-size-16 flex-shrink-0"></i>
                <div className="flex-grow-1">{error}</div>
              </div>
            )}

            {/* Confirmation Note */}
            <div
              className="alert mb-0 py-2.5 px-3 font-size-12 d-flex align-items-center gap-2"
              style={{
                backgroundColor: "rgba(var(--bs-primary-rgb), 0.08)",
                border: "1px solid rgba(var(--bs-primary-rgb), 0.2)",
                color: "var(--bs-emphasis-color)",
                borderRadius: "10px",
              }}
            >
              <i className="bx bx-info-circle font-size-18 text-primary flex-shrink-0"></i>
              <div>
                Click <strong>Claim Reward</strong> to confirm your request. Our team will process your claim shortly.
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div
            className="modal-footer border-top py-3 px-3 px-sm-4 d-flex justify-content-end gap-2"
            style={{
              borderColor: "var(--bs-border-color)",
              backgroundColor: "var(--bs-secondary-bg)",
            }}
          >
            <button
              type="button"
              className="btn btn-light border px-3 flex-grow-1 flex-sm-grow-0"
              onClick={onClose}
              disabled={loading}
              style={{ borderRadius: "8px" }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary fw-bold px-4 flex-grow-1 flex-sm-grow-0 d-flex align-items-center justify-content-center gap-2 shadow-sm"
              disabled={loading}
              onClick={handleClaimSubmit}
              style={{ borderRadius: "8px" }}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span>Claiming...</span>
                </>
              ) : (
                <>
                  <i className="bx bx-gift font-size-16"></i>
                  <span>Claim Reward</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
